import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';

/**
 * The nurse's day, from the nurse-tasks module (backlog §3, built on this
 * branch). Every response is `{ success, data }` — unwrapped here, once.
 */

export type Lane = 'rooms' | 'prep' | 'after';
export type TaskKind =
  | 'dilate' | 'vitals' | 'injection' | 'observe'
  | 'consent' | 'chaperone' | 'dressing' | 'other';
export type TaskState = 'open' | 'in_progress' | 'done' | 'cant';

export interface Person { id: string; firstName: string; lastName: string }

export interface NurseTask {
  id: string;
  lane: Lane;
  kind: TaskKind;
  title: string;
  detail: string | null;
  orderText: string | null;
  /** True when the task is linked to a medication order that Consent refused can block. */
  orderLinked?: boolean;
  orderSignedAt: string | null;
  room: string | null;
  nurseOnly: boolean;
  state: TaskState;
  timerSeconds: number | null;
  startedAt: string | null;
  dueAt: string | null;
  completedAt: string | null;
  outcome: string | null;
  cantReason: string | null;
  cantNote: string | null;
  handedAt: string | null;
  handedByMe: boolean;
  mine: boolean;
  handedFrom: Person | null;
  /** Who closed it (done or Can't). */
  completedBy: Person | null;
  /** Who started its clock — "running · started 7:42 by Hoda S." */
  startedBy: Person | null;
  /** The flow's (or order's) timer before anyone changed it. */
  defaultTimerSeconds: number | null;
  /** How many times it was reopened — each with its reason, on the task. */
  reopened: number;
  assignee: Person | null;
  requestedBy: Person | null;
  patient: (Person & { allergies: string[]; conditions: string[]; age: number | null; sex: 'F' | 'M' | null }) | null;
  allergyCheck: { state: 'none_recorded' | 'clear' | 'conflict'; allergens: string[]; hits: string[] };
  appointment: { id: string; number: number; status: string; arrived: boolean } | null;
  medicalRecordId: string | null;
  courseId: string | null;
  doseNo: number | null;
  doseTotal: number | null;
  scheduledFor: string | null;
  batchId: string | null;
  flow: { name: string; step: number | null; steps: number | null; next: string | null } | null;
  site: string | null;
  batchNo: string | null;
  /** What she saved, for a vitals task that is done. */
  vitals: Record<string, number | string | null> | null;
  /** Per-field verdicts from the reviewed bands, computed at save. */
  flags: Record<string, string> | null;
  createdAt: string;
}

export interface CourseDose {
  id: string;
  doseNo: number | null;
  scheduledFor: string | null;
  state: TaskState;
  completedAt: string | null;
  createdAt: string;
}

export interface MyDay {
  tasks: NurseTask[];
  counts: { open: number; handedOver: number; all: number };
  rooms: Array<Person & { providerId: string; number: number | null }>;
}

export interface Receiver extends Person { role: string; lastActiveAt: string | null }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = <T,>(res: { data: any }): T => (res.data?.data ?? res.data) as T;

export const NURSE_DAY_KEY = ['nurse', 'day'] as const;

export function useMyDay() {
  return useQuery({
    queryKey: NURSE_DAY_KEY,
    queryFn: async () => unwrap<MyDay>(await apiClient.get('/nurse-tasks/my-day')),
    /* The rooms call while she is looking at the list — a stale day is the
       one thing this screen cannot be. */
    refetchInterval: 30_000,
  });
}

export function useNurseTask(id: string | undefined) {
  return useQuery({
    queryKey: ['nurse', 'task', id],
    enabled: Boolean(id),
    queryFn: async () =>
      unwrap<NurseTask & { course: CourseDose[] }>(await apiClient.get(`/nurse-tasks/${id}`)),
  });
}

export function useReceivers() {
  return useQuery({
    queryKey: ['nurse', 'receivers'],
    queryFn: async () => unwrap<Receiver[]>(await apiClient.get('/nurse-tasks/receivers')),
  });
}

/** Every write refreshes the day and the task it touched. */
export function useTaskActions() {
  const qc = useQueryClient();
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: ['nurse'] });
  };

  const start = useMutation({
    mutationFn: (id: string) => apiClient.patch(`/nurse-tasks/${id}/start`),
    onSuccess,
  });
  const setTimer = useMutation({
    mutationFn: (a: { id: string; seconds: number; note?: string }) =>
      apiClient.patch(`/nurse-tasks/${a.id}/timer`, { seconds: a.seconds, note: a.note || undefined }),
    onSuccess,
  });
  const complete = useMutation({
    mutationFn: ({ id, ...body }: { id: string; outcome?: string; site?: string; batchNo?: string; note?: string }) =>
      apiClient.patch(`/nurse-tasks/${id}/done`, body),
    onSuccess,
  });
  const cant = useMutation({
    mutationFn: (a: { id: string; reason: string; note?: string }) =>
      apiClient.patch(`/nurse-tasks/${a.id}/cant`, { reason: a.reason, note: a.note || undefined }),
    onSuccess,
  });
  /* Reopening asks for a reason — the log keeps both lines. */
  const reopen = useMutation({
    mutationFn: (a: { id: string; reason: string }) =>
      apiClient.patch(`/nurse-tasks/${a.id}/reopen`, { reason: a.reason }),
    onSuccess,
  });
  const handOver = useMutation({
    mutationFn: (a: { toUserId: string; all?: boolean; taskIds?: string[] }) =>
      apiClient.post('/nurse-tasks/hand-over', a),
    onSuccess,
  });
  const flagLow = useMutation({
    mutationFn: (line: string) => apiClient.post('/nurse-tasks/flag-low', { line }),
    onSuccess,
  });
  const saveVitals = useMutation({
    mutationFn: async (a: { id: string; values: Record<string, number | string | undefined> }) =>
      unwrap<{ critical: boolean; flags: Record<string, string> }>(
        await apiClient.post(`/nurse-tasks/${a.id}/vitals`, a.values),
      ),
    onSuccess,
  });

  return { start, setTimer, complete, cant, reopen, handOver, flagLow, saveVitals };
}

/**
 * CAPTURE — a photo or a file onto the patient's documents, under the task's
 * own gate (the Nurse role has no `patient:write` for the general route).
 * Presign on the backend, PUT straight to storage, then finalize.
 */
export async function captureFile(taskId: string, file: File): Promise<{ attachmentId: string; state: TaskState }> {
  const mimeType = file.type || 'application/octet-stream';
  const presign = unwrap<{ attachmentId: string; uploadUrl: string; requiredHeaders: Record<string, string> }>(
    await apiClient.post(`/nurse-tasks/${taskId}/capture/presign`, {
      originalName: file.name,
      mimeType,
      sizeBytes: file.size,
    }),
  );
  const put = await fetch(presign.uploadUrl, { method: 'PUT', headers: presign.requiredHeaders, body: file });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  const etag = put.headers.get('ETag')?.replace(/^"|"$/g, '') ?? undefined;
  return unwrap(await apiClient.post(`/nurse-tasks/${taskId}/capture/${presign.attachmentId}/finalize`, { etag }));
}
